import { DateTime } from 'luxon'

import { TimeFieldDefinition } from '../../definitions/field-definitions/time-field-definition'
import { isString } from '../../utils'
import { FieldValue } from './field.class'

export class TimeValue extends FieldValue<DateTime> {
  /**
   * This expects the value to be a string formatted as HH:mm:ss
   * or a DateTime that has only the time part, the date part should be left empty
   * and default to 1970-1-1
   */
  constructor(value?: string | DateTime) {
    super()
    if (value && isString(value)) {
      this.value = this.valueFromString(value)
    } else if (value && value instanceof DateTime) {
      this.value = this.valueFromDateTime(value)
    } else if (value) {
      throw new Error(`[TimeValue] can't construct TimeValue unsupported value: ${typeof value}`)
    }
  }

  public mapField(field: TimeValue, fieldDef: TimeFieldDefinition): void {
    if (field.value && isString(field.value)) {
      this.value = this.valueFromString(field.value)
    }
  }

  public toStringForView(locale?: string): string {
    const valueToStringify = (this.value as DateTime)?.setLocale(locale ?? 'en-US')

    let format: Record<string, 'numeric'> = {
      hour: 'numeric',
      minute: 'numeric',
    }
    if ((this.value as DateTime)?.second > 0) {
      format = {
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
      }
    }

    return valueToStringify.toLocaleString(format)
  }

  private valueFromString(value: string): DateTime {
    // eslint-disable-next-line no-useless-escape
    const ISO_RE = /^\d{4}-\d{2}-\d{2}T([01]\d|2[0-3]):([0-5]\d):([0-5]\d)(?:\.\d+)?(?:Z|[+\-]\d{2}:\d{2})?$/

    if (ISO_RE.test(value)) {
      const parsed = DateTime.fromISO(value, { zone: 'utc' })
      const res = DateTime.fromObject(
        {
          year: 1970,
          month: 1,
          day: 1,
          hour: parsed.hour,
          minute: parsed.minute,
          second: parsed.second,
          millisecond: parsed.millisecond,
        },
        { zone: 'utc' },
      )
      return res
    }

    throw new Error(
      `[TimeValue] invalid time format "${value}". Expected "HH:mm:ss" or an ISO datetime like "YYYY-MM-DDTHH:mm:ss".`,
    )
  }

  private valueFromDateTime(value: DateTime) {
    const res = DateTime.fromObject(
      { year: 1970, month: 1, day: 1, hour: value.hour, minute: value.minute, second: value.second },
      { zone: 'utc' },
    )
    return res
  }
}
